import clsx from 'clsx';
import OverlayV2 from 'components-v2/atoms/OverlayV2';
import LinkComp from 'components/atoms/LinkComp';
import Image from 'next/image';
import { useRouter } from 'next/router';
import React, { memo, useCallback, useRef, useState } from 'react';
import gtag from 'utils/gtag';
import { getLinkCDNImage } from 'utils/ImageUtils';
import styles from './styles.module.css';

/**
 * Category Interface (Level 3 items)
 * Represents a category item displayed in the right panel grid
 */
interface Category {
  categoryID?: number;
  code?: string;
  description?: string;
  icon?: string;
  isActive?: boolean;
  name?: string;
  priority?: number;
  slug?: string;
  systemDisplay?: string;
  iconCategory?: string;
}

/**
 * SubMenuItem Interface
 * Represents a menu item that can have nested sub-items or categories
 *
 * Structure:
 * - Level 1: Navbar trigger (e.g., "Products", "Services")
 * - Level 2: Items displayed in the left panel of the submenu (SubMenuItems)
 * - Level 3: Categories displayed in the right panel grid (Categories)
 */
interface SubMenuItem {
  url: string;                // Navigation URL when item is clicked
  iconUrl?: string;           // CDN URL for the item icon
  iconWebUrl?: string;        // Alternative web URL for the icon
  iconWebActiveUrl?: string;  // Active state icon URL
  iconActiveUrl?: string;     // Active state icon URL (alternative field name)
  label: string;              // Display text for the menu item
  isActive?: boolean;         // Whether this item is currently active
  subMenuItems?: SubMenuItem[]; // Level 2 items (nested menu items)
  categories?: Category[];    // Level 3 categories - displayed in right panel grid
  mapParams?: Record<string, string>; // Map params from URL
  badgeIconUrl?: string;      // Badge icon URL displayed next to label
}

/**
 * Props for SubMenuV2 Component
 *
 * @param items - Array of Level 2 menu items to display in the left panel
 * @param children - The trigger element (usually a button or link) that opens the menu
 */
interface Props {
  items: SubMenuItem[];
  children: React.ReactNode;
}

/**
 * SubMenuV2 Component
 *
 * A two-panel dropdown menu component that displays:
 * - Left Panel: Level 2 items (categories)
 * - Right Panel: Level 3 items (products/services under selected category)
 *
 * Features:
 * - Hover-based navigation between left and right panels
 * - Conditional styling based on whether items have sub-items
 * - Analytics tracking for menu interactions
 * - Smooth transitions and visual feedback
 */

/**
   * eventRef
   * Prevents duplicate analytics events when menu opens
   * Set to true after first open, reset when menu closes
*/


/**
 * hasAnySubItems: 
 * Check if ANY Level 2 item has Level 3 sub-items
 * Used to conditionally apply CSS styling:
 * - When true: removes right padding and border-radius from left panel
 * - When false: keeps default padding and rounded corners
 */
function SubMenuV2({ items, children }: Props) {
  const router = useRouter();
  const [isOpen, setIsOpen] = useState(false);
  const [activeItemIndex, setActiveItemIndex] = useState<number | null>(null);
  const [loadingImageIndex, setLoadingImageIndex] = useState<number | null>(null);
  const eventRef = useRef(false);

  /** Get the currently active Level 2 item based on activeItemIndex */
  const activeItem = activeItemIndex !== null ? items[activeItemIndex] : null;

  /** Get Level 3 items (categories) from the active Level 2 item */
  const level3Items = activeItem?.categories || [];

  const handleOpenMenu = useCallback(() => {
    setIsOpen(true);
    if (!eventRef.current) {
      // Track analytics only on first open
      items.forEach((item) => {
        gtag.showSubMenu(item);
      });
      eventRef.current = true;
    }
  }, [items]);

  const handleCloseMenu = useCallback(() => {
    setIsOpen(false);
    setActiveItemIndex(null);
    eventRef.current = false;
  }, []);

  const handleLevel3ItemClick = useCallback((item: Category) => {
    // Replace filter with new level3 item (don't add, replace)
    const newFilter = {
      categoryFilters: [
        {
          code: item?.code,
          categoryCodes: [item?.code],
          efficacyCodes: [],
        },
      ],
    };

    if (router.query?.currentTab) {
      // delete current tab on query
      delete router.query.currentTab;
    }

    if (router.query?.slug) {
      delete router.query.slug;
    }

    const newQuery = {
      ...router.query,
      filter: JSON.stringify(newFilter),
      tag: activeItem?.mapParams?.tag,
      page: 1,
    };

    router.push(
      {
        pathname: '/products',
        query: newQuery,
      },
      undefined,
      { scroll: false, shallow: false }
    );

    gtag.clickSubMenubar(item);
    handleCloseMenu();
  }, [router, activeItem, handleCloseMenu]);

  return (
    <>
      <div
        className={clsx(styles.MenuTrigger, { [styles.isActiveMenu]: isOpen })}
        onMouseEnter={handleOpenMenu}
        onMouseLeave={handleCloseMenu}
      >
        {children}

        {isOpen && (
          <div className={styles.MenuV2} onMouseLeave={() => {
            setActiveItemIndex(null);
            handleCloseMenu();
          }}>
            <div className={styles.MenuV2Wrapper}>
              <div className={styles.SubMenuContainer}>
                {items.length > 0 && (
                  <div className={styles.LeftPanel}>
                    {items.map((item, index) => {
                      // Check if this specific item has Level 3 categories
                      const isActive = activeItemIndex === index;

                      return (
                        <div
                          key={`${item.label}-${item.url}`}
                          onMouseEnter={() => {
                            setActiveItemIndex(index);
                            setLoadingImageIndex(index);
                          }}
                        >
                          <LinkComp
                            removeStyles
                            href={item?.url || "#"}
                            target={undefined}
                            className={clsx(styles.LeftPanelItem, {
                              // Active state: applied when item is hovered
                              [styles.LeftPanelItemActive]: isActive,
                              // Conditional styling: applied when any item has sub-items
                              [styles.LeftPanelItemWithSubItems]: item?.categories && item.categories.length > 0,
                            })}
                            onClick={() => {
                              gtag.clickSubMenubar(item);
                              handleCloseMenu();
                            }}
                          >
                            <div className={styles.LeftPanelItemContent}>
                              {item?.iconUrl && (
                                <div className={styles.LeftPanelIcon}>
                                  <Image
                                    width="24"
                                    height="24"
                                    src={getLinkCDNImage(isActive && item?.iconActiveUrl ? item.iconActiveUrl : item.iconUrl)}
                                    alt={item.label}
                                    unoptimized
                                    style={{
                                      opacity: loadingImageIndex === index ? 0.5 : 1,
                                      transition: 'opacity 0.3s ease-in-out'
                                    }}
                                    onLoadingComplete={() => setLoadingImageIndex(null)}
                                  />
                                </div>
                              )}
                              <div className={styles.LeftPanelLabel}>
                                <span>{item.label}</span>
                                {item?.badgeIconUrl && (
                                  <span className={styles.BadgeIcon}>
                                    <Image
                                      width="16"
                                      height="16"
                                      src={getLinkCDNImage(item.badgeIconUrl)}
                                      alt="badge"
                                      unoptimized
                                    />
                                  </span>
                                )}
                              </div>

                            </div>
                          </LinkComp>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/*
                ============ RIGHT PANEL - LEVEL 3 ITEMS GRID ============
                Displays a grid of products/services under the selected category

                Visibility:
                - Only shown when:
                  1. activeItem exists (a Level 2 item is hovered)
                  2. level3Items has items (activeItem has sub-items)

                Layout: 5-column grid with icons and labels
              */}

                {activeItem && level3Items.length > 0 && (
                  <div className={styles.RightPanel}>
                    <div className={styles.RightPaneCover}>
                      {level3Items.map((item) => (
                        <div
                          key={`${item?.code}-${item?.categoryID}`}
                          className={styles.RightPanelItem}
                          onClick={() => handleLevel3ItemClick(item)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              handleLevel3ItemClick(item);
                            }
                          }}
                        >
                          <div className={styles.RightPanelItemContent}>
                            {item?.iconCategory && (
                              <div className={styles.RightPanelIcon}>
                                <Image
                                  width="60"
                                  height="60"
                                  src={getLinkCDNImage(item?.iconCategory)}
                                  alt={item?.name}
                                />
                              </div>
                            )}
                            <span className={styles.RightPanelLabel}>{item?.name}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div >

      <OverlayV2 isOpen={isOpen} level={1} />
    </>
  );
}

export default memo(SubMenuV2);
